// First-run onboarding — a single-page checklist.
//
//   1 · Install dense        copy → run it → dense's login shows up here
//   2 · Choose your agent    explicit pick, no default        → advances
//   3 · Start <agent>        run it once; the first request finishes it
//
// Step 1 completes on a REAL signal (the `dense setup` device-flow key
// landing on /me), with "I ran it" as the manual floor — copy alone never
// advances. Step 3 waits on usage.request_count rising above the baseline
// snapshotted at mount, so a returning user's history never auto-skips.

import React from "react";
import { Brand } from "../components/Brand";
import { CopyPill } from "../components/CopyPill";
import { Modal } from "../components/Modal";
import { Button } from "../design/Button";
import { Icon } from "../design/icons";
import { detectPlatform, type Platform } from "../lib/platform";
import { cliUrl } from "../lib/urls";
import { ackOnboarding, deviceLink } from "../lib/api";
import {
  clearWizard,
  denseKeyIds,
  denseLinked,
  HARNESSES,
  OTHER_HARNESS,
  resolveHarness,
  type Harness,
  WIZARD_KEY,
} from "../lib/onboarding";
import type { AuthHeaders } from "../lib/auth";
import type { Me } from "../lib/types";

type Step = 1 | 2 | 3;

function requestCount(me: Me): number {
  return me.usage?.request_count ?? 0;
}

// ── resume persistence ──────────────────────────────────────────────
// /cli (the printed sign-in link, if someone opens it) reads this key to
// name the chosen agent; a same-tab return or refresh restores step + picks.
type WizardState = {
  step: Step;
  os: Platform;
  harnessId: string;
  copied: boolean;
};

function loadWizard(): Partial<WizardState> {
  try {
    const raw = localStorage.getItem(WIZARD_KEY);
    return raw ? (JSON.parse(raw) as Partial<WizardState>) : {};
  } catch {
    return {};
  }
}
function saveWizard(s: WizardState): void {
  try {
    localStorage.setItem(WIZARD_KEY, JSON.stringify(s));
  } catch {
    /* private mode / storage disabled — resume is best-effort */
  }
}

// ── install command per platform ────────────────────────────────────
function installCmd(os: Platform): {
  command: string;
  host: string;
  prompt: string;
} {
  const base = cliUrl();
  // CONDENSE_NO_OPEN: `dense setup` prints the sign-in code instead of opening
  // a second tab — this page is where the code gets entered.
  return os === "windows"
    ? {
        command: `$env:CONDENSE_NO_OPEN=1; irm ${base}/nt | iex`,
        host: `${base}/nt`,
        prompt: "PS>",
      }
    : {
        command: `curl -fsSL ${base}/unix | CONDENSE_NO_OPEN=1 sh`,
        host: `${base}/unix`,
        prompt: "$",
      };
}

// Server matches the code verbatim (`ABCD-EFGH`); accept it typed without the dash.
function normCode(raw: string): string {
  const s = raw.toUpperCase().replace(/[^A-Z]/g, "");
  return s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4)}` : raw.trim();
}

type StepStatus = "pending" | "active" | "done";
function statusCls(s: StepStatus): string {
  return s === "done" ? "is-done" : s === "active" ? "is-active" : "is-pending";
}

// ── OS segmented control ────────────────────────────────────────────
function OsSeg({ os, setOs }: { os: Platform; setOs: (p: Platform) => void }) {
  return (
    <div className="ob-seg" role="tablist" aria-label="Operating system">
      <button
        type="button"
        role="tab"
        aria-selected={os === "unix"}
        className={os === "unix" ? "on" : ""}
        onClick={() => setOs("unix")}
      >
        mac · linux
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={os === "windows"}
        className={os === "windows" ? "on" : ""}
        onClick={() => setOs("windows")}
      >
        windows
      </button>
    </div>
  );
}

// ── live terminal preview — mirrors the same progression ────────────
function Terminal({
  step,
  os,
  harness,
  copied,
  linked,
  live,
  email,
}: {
  step: Step;
  os: Platform;
  harness: Harness;
  copied: boolean;
  linked: boolean;
  live: boolean;
  email: string | null;
}) {
  const install = installCmd(os);
  const ran = step >= 2 || copied; // install one-liner is in their terminal
  const wrapping = step >= 3; // agent chosen, wrap command surfaced
  const start = harness.tool ?? harness.wrap;

  return (
    <div className="ob-term" aria-hidden="true">
      <div className="ob-term-bar">
        <div className="ob-dots">
          <i />
          <i />
          <i />
        </div>
        <div className="ob-term-title">zsh — acme-app</div>
        <div className="ob-watch">
          <b />
          WATCHING
        </div>
      </div>

      <div className="ob-term-body">
        {ran ? (
          <>
            <div className="tl">
              <span className="tl-prompt">~ $ </span>
              <span className="tl-cmd">{install.command}</span>
            </div>
            <div className="tl tl-dim">downloading dense … done</div>
            <div className="tl tl-ok">
              <Icon name="check" size={12} /> installed to ~/.dense/bin
            </div>
            <div className="tl tl-dim">dense setup · Code: ABCD-EFGH</div>
            {linked || step >= 2 ? (
              <div className="tl tl-ok">
                ✓ authorized{email ? ` as ${email}` : ""}
              </div>
            ) : (
              <div className="tl">
                <span className="tl-dim">waiting for authorization </span>
                <span className="tl-cursor" />
              </div>
            )}
            {wrapping ? (
              <>
                <div className="tl gap" />
                <div className="tl tl-dim">
                  next: run `{start}` inside a project
                </div>
                <div className="tl gap" />
                <div className="tl">
                  <span className="tl-path">~/acme-app $ </span>
                  {live ? (
                    <span className="tl-cmd">{start}</span>
                  ) : (
                    <span className="tl-cursor" />
                  )}
                </div>
                {live ? (
                  <div className="tl tl-ok">
                    ✓ session live — compressing every call
                  </div>
                ) : null}
              </>
            ) : step >= 2 ? (
              <>
                <div className="tl gap" />
                <div className="tl">
                  <span className="tl-prompt">~ $ </span>
                  <span className="tl-cursor" />
                </div>
              </>
            ) : null}
          </>
        ) : (
          <div className="tl">
            <span className="tl-prompt">~ $ </span>
            <span className="tl-cursor" />
          </div>
        )}
      </div>

      <div
        className={`ob-term-status ${live || (step === 1 && linked) ? "live" : ""}`}
      >
        <i />
        {live ? (
          <span>session live — savings streaming in</span>
        ) : wrapping ? (
          <span>
            waiting for <code>{start}</code> … copy it on the left
          </span>
        ) : step === 2 ? (
          <span>installed — now choose your agent on the left</span>
        ) : linked ? (
          <span>dense signed in from your terminal — continue on the left</span>
        ) : copied ? (
          <span>copied — run it, then type the code it prints on the left</span>
        ) : (
          <span>waiting for the install command … copy it on the left</span>
        )}
      </div>
    </div>
  );
}

// ── shell ───────────────────────────────────────────────────────────
export function Onboarding({
  me,
  authHeaders,
  refresh,
  onComplete,
}: {
  me: Me;
  authHeaders: AuthHeaders;
  refresh: () => void | Promise<void>;
  onComplete: () => void;
}) {
  const [restored] = React.useState<Partial<WizardState>>(loadWizard);
  const [harnessId, setHarnessId] = React.useState<string>(
    restored.harnessId ?? "",
  );
  // A restored step 3 with no pick (pre-fix state) drops back to choosing.
  const [step, setStep] = React.useState<Step>(
    restored.step === 3 && restored.harnessId
      ? 3
      : restored.step === 2 || restored.step === 3
        ? 2
        : 1,
  );
  const [os, setOs] = React.useState<Platform>(restored.os ?? detectPlatform());
  const [copied, setCopied] = React.useState<boolean>(restored.copied ?? false);
  const [entering, setEntering] = React.useState(false);
  // Baseline: only requests that arrive AFTER this mount count as the
  // first real session — a returning user's history never auto-completes.
  const [baseReq] = React.useState(() => requestCount(me));
  const [baseKeys] = React.useState(() => new Set(denseKeyIds(me)));

  React.useEffect(() => {
    saveWizard({ step, os, harnessId, copied });
  }, [step, os, harnessId, copied]);

  const live = requestCount(me) > baseReq;
  const linked = denseLinked(me, baseKeys);
  const harness = resolveHarness(harnessId);
  const install = installCmd(os);
  // Until the agent step is passed, the wrap step is generic.
  const wrapName = step >= 3 ? harness.name : "your agent";

  const advance = (to: Step) => setStep((s) => (s < to ? to : s));

  // `dense setup` signed this account in from the terminal — install is proven.
  React.useEffect(() => {
    if (linked && step === 1) advance(2);
  }, [linked, step]);

  const installStatus: StepStatus = step > 1 ? "done" : "active";
  const agentStatus: StepStatus =
    step > 2 ? "done" : step === 2 ? "active" : "pending";
  const wrapStatus: StepStatus = live
    ? "done"
    : step === 3
      ? "active"
      : "pending";

  const pickAgent = (id: string) => {
    setHarnessId(id);
    advance(3);
  };

  const finish = React.useCallback(async () => {
    setEntering(true);
    try {
      await ackOnboarding(authHeaders, "onboarding_complete");
    } catch {
      /* best-effort — never trap the user in setup */
    }
    clearWizard();
    await refresh();
    onComplete();
  }, [authHeaders, refresh, onComplete]);

  // Inline device-flow approval: same endpoint as /cli, no second tab.
  const [code, setCode] = React.useState("");
  const [codeErr, setCodeErr] = React.useState("");
  const [linking, setLinking] = React.useState(false);
  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLinking(true);
    setCodeErr("");
    try {
      const r = await deviceLink(authHeaders, normCode(code));
      if (r.ok) {
        advance(2);
        void refresh();
      } else {
        setCodeErr(
          r.status === 404
            ? "That code is expired or already used — re-run dense setup for a new one."
            : `Server error ${r.status}`,
        );
      }
    } catch {
      setCodeErr("Network error — try again.");
    } finally {
      setLinking(false);
    }
  };

  const back = () => setStep((s) => (s > 1 ? ((s - 1) as Step) : s));
  const gated = !live;

  // "I ran it" is not proof. The dialog keeps watching for the sign-in and
  // only lets a manual continue through after both checks are ticked.
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [ranOk, setRanOk] = React.useState(false);
  const [signedOk, setSignedOk] = React.useState(false);
  const closeConfirm = React.useCallback(() => setConfirmOpen(false), []);
  const confirmInstall = () => {
    setConfirmOpen(false);
    advance(2);
  };

  return (
    <div className="ob-shell">
      <header className="ob-topbar">
        <div className="ob-brand">
          <Brand />
        </div>
        <button
          type="button"
          className="ob-skip"
          onClick={finish}
          disabled={entering}
        >
          Skip setup
        </button>
      </header>

      <main className="ob-main">
        <div className="ob-left">
          <div className="ob-eyebrow">Get set up</div>
          <h1 className="ob-h1">Follow along in your terminal</h1>

          <div className="ob-list">
            {/* Step 1 · Install */}
            <div className={`ob-step ${statusCls(installStatus)}`}>
              <div className="ob-marker">
                <span className="ob-dot">
                  {installStatus === "done" ? (
                    <Icon name="check" size={12} />
                  ) : (
                    "1"
                  )}
                </span>
                <span className="ob-rail" />
              </div>
              <div className="ob-stepbody">
                <div className="ob-steptitle">Install dense</div>
                {installStatus === "done" ? (
                  <div className="ob-stepmeta">
                    {linked ? "Signed in from your terminal" : "Installed"}
                  </div>
                ) : (
                  <div className="ob-stepaction">
                    <OsSeg os={os} setOs={setOs} />
                    <CopyPill
                      command={install.command}
                      host={install.host}
                      prompt={install.prompt}
                      onCopied={() => setCopied(true)}
                    />
                    {copied ? (
                      <>
                        <div className="ob-wait">
                          <i />
                          Paste it in your terminal. It prints a sign-in code —
                          enter it here and dense is signed in.
                        </div>
                        <form className="ob-code" onSubmit={submitCode}>
                          <input
                            className="text-input mono"
                            value={code}
                            onChange={(e) => {
                              setCode(e.target.value.toUpperCase());
                              setCodeErr("");
                            }}
                            placeholder="ABCD-EFGH"
                            maxLength={9}
                            spellCheck={false}
                            autoCapitalize="characters"
                            aria-label="Sign-in code from your terminal"
                          />
                          <Button
                            type="submit"
                            variant="primary"
                            disabled={linking || normCode(code).length !== 9}
                          >
                            {linking ? "Signing in…" : "Sign in"}
                          </Button>
                        </form>
                        {codeErr ? (
                          <div className="form-err">{codeErr}</div>
                        ) : null}
                      </>
                    ) : (
                      <div className="ob-stepmeta">
                        Copy, paste into a terminal, then come back here.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Step 2 · Choose agent */}
            <div className={`ob-step ${statusCls(agentStatus)}`}>
              <div className="ob-marker">
                <span className="ob-dot">
                  {agentStatus === "done" ? (
                    <Icon name="check" size={12} />
                  ) : (
                    "2"
                  )}
                </span>
                <span className="ob-rail" />
              </div>
              <div className="ob-stepbody">
                <div className="ob-steptitle">Choose your coding agent</div>
                {agentStatus === "done" ? (
                  <div className="ob-stepmeta">{harness.name}</div>
                ) : agentStatus === "active" ? (
                  <div className="ob-stepaction">
                    <div
                      className="ob-agents"
                      role="radiogroup"
                      aria-label="Coding agent"
                    >
                      {HARNESSES.map((h) => (
                        <button
                          type="button"
                          key={h.id}
                          role="radio"
                          aria-checked={harnessId === h.id}
                          className="ob-agent"
                          onClick={() => pickAgent(h.id)}
                        >
                          <span className="ob-agent-badge">
                            {h.logo ? (
                              <img src={h.logo} alt="" width={20} height={20} />
                            ) : (
                              <span aria-hidden="true">+</span>
                            )}
                          </span>
                          <span className="ob-agent-name">{h.name}</span>
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="ob-agent-other"
                      onClick={() => pickAgent(OTHER_HARNESS.id)}
                    >
                      I use a different agent →
                    </button>
                  </div>
                ) : (
                  <div className="ob-stepmeta">
                    dense wraps the agent you already run
                  </div>
                )}
              </div>
            </div>

            {/* Step 3 · Wrap */}
            <div className={`ob-step ${statusCls(wrapStatus)}`}>
              <div className="ob-marker">
                <span className="ob-dot">
                  {wrapStatus === "done" ? (
                    <Icon name="check" size={12} />
                  ) : (
                    "3"
                  )}
                </span>
                <span className="ob-rail" />
              </div>
              <div className="ob-stepbody">
                <div className="ob-steptitle">Start {wrapName}</div>
                {wrapStatus === "active" ? (
                  <div className="ob-stepaction">
                    <div className="ob-stepmeta">
                      Open a project and start it. Setup already routes the bare
                      command through dense if you ticked it:
                    </div>
                    <CopyPill
                      command={harness.tool ?? harness.wrap}
                      prompt="$"
                    />
                    {harness.tool ? (
                      <div className="ob-stepmeta">
                        Didn’t tick it? <code>{harness.wrap}</code> works per
                        session, or run{" "}
                        <code>dense persist {harness.tool}</code> once.
                      </div>
                    ) : null}
                    <div className="ob-wait">
                      <i />
                      Waiting for your first request — send one message and this
                      step ticks itself off.
                    </div>
                  </div>
                ) : (
                  <div className="ob-stepmeta">
                    {wrapStatus === "done"
                      ? "First request came through — you're done"
                      : "Run it once; the first request finishes setup"}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="ob-foot">
            <button
              type="button"
              className="ob-back"
              onClick={back}
              disabled={entering || step === 1}
            >
              ← Back
            </button>
            <div className="ob-foot-right">
              {step === 1 ? (
                <button
                  type="button"
                  className="ob-primary"
                  onClick={() => setConfirmOpen(true)}
                  disabled={!copied}
                  title={
                    copied ? undefined : "Copy the command and run it first"
                  }
                >
                  I ran it — continue
                </button>
              ) : step === 2 ? (
                <span className="ob-stepmeta">Pick an agent to continue</span>
              ) : (
                <>
                  <button
                    type="button"
                    className="ob-skipstep"
                    onClick={() => void finish()}
                    disabled={entering}
                  >
                    Skip for now →
                  </button>
                  <button
                    type="button"
                    className="ob-primary"
                    onClick={finish}
                    disabled={entering || gated}
                    title={
                      gated
                        ? "Send one message through dense to continue"
                        : undefined
                    }
                  >
                    {entering ? "Entering…" : "Enter dashboard"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="ob-right">
          <Terminal
            step={step}
            os={os}
            harness={harness}
            copied={copied}
            linked={linked}
            live={live}
            email={me.email ?? null}
          />
        </div>
      </main>

      {confirmOpen && step === 1 ? (
        <Modal
          eyebrow="Step 1"
          title="Did dense sign in?"
          onClose={closeConfirm}
          footer={
            <>
              <Button variant="ghost" onClick={closeConfirm}>
                Not yet, go back
              </Button>
              <Button
                variant="primary"
                onClick={confirmInstall}
                disabled={!(ranOk && signedOk)}
              >
                Yes, I signed in, continue
              </Button>
            </>
          }
        >
          <div className="ob-wait">
            <i />
            Still watching for the sign-in from your terminal. This closes on
            its own the moment it lands.
          </div>
          <p className="ob-confirm-why">
            The installer ends by running <code>dense setup</code>, which prints
            a sign-in code. Until that code is entered here (or its link
            approved), <code>dense</code> can’t route your agent through
            condense.
          </p>
          <label className="ob-check">
            <input
              type="checkbox"
              checked={ranOk}
              onChange={(e) => setRanOk(e.target.checked)}
            />
            The install command finished without errors
          </label>
          <label className="ob-check">
            <input
              type="checkbox"
              checked={signedOk}
              onChange={(e) => setSignedOk(e.target.checked)}
            />
            <span>
              <code>dense setup</code> printed a code and I approved it (here or
              via its link)
            </span>
          </label>
        </Modal>
      ) : null}
    </div>
  );
}
